import React, { Suspense, useEffect } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { RestaurantProvider } from "@/contexts/RestaurantContext";
import { CMVRecalculationProvider } from "@/contexts/CMVRecalculationContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { OnboardingGuard } from "@/components/OnboardingGuard";
import ErrorBoundary from "@/components/ErrorBoundary";
import { createIDBPersister } from '@/lib/idbPersister';
import { APP_VERSION } from '@/lib/cacheManager';
import { ROUTES } from '@/config/routes';
import { lazyWithRetry } from '@/lib/lazyWithRetry';

const Landing = lazyWithRetry(() => import("./pages/Landing"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));

// Lazy load protected routes for better performance
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Inventory = lazyWithRetry(() => import("./pages/Inventory"));
const Menu = lazyWithRetry(() => import("./pages/Menu"));
const Costs = lazyWithRetry(() => import("./pages/Costs"));
const Reports = lazyWithRetry(() => import("./pages/Reports"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const ExecutiveDashboard = lazyWithRetry(() => import("./pages/ExecutiveDashboard"));
const OnboardingWizard = lazyWithRetry(() => import("./pages/OnboardingWizard"));
const MenuEngineeringReport = lazyWithRetry(() => import("./pages/MenuEngineeringReport"));

const PriceReviewReport = lazyWithRetry(() => import("./pages/PriceReviewReport"));
const CMVDiagnosticReport = lazyWithRetry(() => import("./pages/CMVDiagnosticReport"));
const Stock = lazyWithRetry(() => import("./pages/Stock"));
const Categories = lazyWithRetry(() => import("./pages/Categories"));
// Subscription page removed - consolidated into Settings tab
const Checkout = lazyWithRetry(() => import("./pages/Checkout"));
const AddonCheckout = lazyWithRetry(() => import("./pages/AddonCheckout"));
const CheckoutSuccess = lazyWithRetry(() => import("./pages/CheckoutSuccess"));

// Sales module pages
const SalesAlaCarte = lazyWithRetry(() => import("./pages/sales/SalesAlaCarte"));
const SalesRodizio = lazyWithRetry(() => import("./pages/sales/SalesRodizio"));
const SalesWeight = lazyWithRetry(() => import("./pages/sales/SalesWeight"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

// Approval flow pages
const PendingApproval = lazyWithRetry(() => import("./pages/PendingApproval"));
const AccessDenied = lazyWithRetry(() => import("./pages/AccessDenied"));
const ApprovalSuccess = lazyWithRetry(() => import("./pages/ApprovalSuccess"));
const PermissionDenied = lazyWithRetry(() => import("./pages/PermissionDenied"));
const AccountSuspended = lazyWithRetry(() => import("./pages/AccountSuspended"));

// Subscription Gate
const SubscriptionGate = lazyWithRetry(() => import("@/components/subscription/SubscriptionGate").then(m => ({ default: m.SubscriptionGate })));

// Lazy load support pages
const SupportLogin = lazyWithRetry(() => import("./pages/support/SupportLogin"));
const SupportDashboard = lazyWithRetry(() => import("./pages/support/SupportDashboard"));
const SupportLayout = lazyWithRetry(() => import("./pages/support/SupportLayout"));
const SupportClients = lazyWithRetry(() => import("./pages/support/SupportClients"));
const SupportMetricsPage = lazyWithRetry(() => import("./pages/support/SupportMetricsPage"));
const SupportNotifications = lazyWithRetry(() => import("./pages/support/SupportNotifications"));
const SupportAuditLogs = lazyWithRetry(() => import("./pages/support/SupportAuditLogs"));

const SupportNotificationsMetrics = lazyWithRetry(() => import("./pages/support/SupportNotificationsMetrics"));
const SupportTickets = lazyWithRetry(() => import("./pages/support/SupportTickets"));
const SupportApprovals = lazyWithRetry(() => import("./pages/support/SupportApprovals"));
const SupportCoupons = lazyWithRetry(() => import("./pages/support/SupportCoupons"));
const SupportReferrals = lazyWithRetry(() => import("./pages/support/SupportReferrals"));
const SupportRetentionOffers = lazyWithRetry(() => import("./pages/support/SupportRetentionOffers"));
const ClientDetails = lazyWithRetry(() => import("@/components/support/ClientDetails").then(m => ({ default: m.ClientDetails })));
const SupportGuard = lazyWithRetry(() => import("@/components/SupportGuard").then(m => ({ default: m.SupportGuard })));
const OrganizationHub = lazyWithRetry(() => import("./components/hub/OrganizationHub").then(m => ({ default: m.OrganizationHub })));
const BlingCallback = lazyWithRetry(() => import("./pages/integrations/BlingCallback"));
const ContaAzulCallback = lazyWithRetry(() => import("./pages/integrations/ContaAzulCallback"));

// Create persister for React Query cache
const persister = createIDBPersister('alacart-react-query');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Não retry para erros de React internal
        if (error instanceof Error && error.message?.includes('Should have a queue')) {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: 60000, // 1 minuto de cache padrão (otimizado)
      gcTime: 30 * 60 * 1000, // 30 minutes - libera memoria de queries inativas (IndexedDB persister mantém 12h entre sessões)
      refetchOnWindowFocus: false, // Evita re-fetches inesperados
      refetchOnReconnect: 'always', // Sempre recarrega ao reconectar
    },
  },
});

// Register Service Worker (silent in production)
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          if (import.meta.env.DEV) {
            console.log('SW registered:', registration.scope);
          }
        })
        .catch((error) => {
          if (import.meta.env.DEV) {
            console.warn('SW registration failed:', error);
          }
        });
    });
  }
};

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Main App Routes (inside RestaurantProvider with RealtimeProvider)
const MainAppRoutes = () => (
  <RestaurantProvider>
    <RealtimeProvider>
      <Routes>
        <Route path={ROUTES.app.dashboard} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Dashboard />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.inventory} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Inventory />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        {/* Removed orphan routes /entries and /recipes - they were duplicating Dashboard */}
        <Route path={ROUTES.app.stock} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Stock />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.categories} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Categories />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.menu} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Menu />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.costs} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Costs />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.sales.root} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <SalesAlaCarte />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.sales.alacarte} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <SalesAlaCarte />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.sales.rodizio} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <SalesRodizio />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.sales.weight} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <SalesWeight />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.reports.root} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Reports />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.settings} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Settings />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.executive} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <ExecutiveDashboard />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.reports.menuEngineering} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <MenuEngineeringReport />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.reports.priceReview} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <PriceReviewReport />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.reports.cmvDiagnostic} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <CMVDiagnosticReport />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        {/* Legacy route redirect to new structure */}
        <Route path="/production-by-weight" element={
          <Navigate to={ROUTES.app.sales.weight} replace />
        } />
        {/* Legacy /subscription redirect to settings tab */}
        <Route path="/subscription" element={
          <Navigate to="/settings?tab=subscription" replace />
        } />
        <Route path={ROUTES.app.checkout} element={
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.addonCheckout} element={
          <ProtectedRoute>
            <AddonCheckout />
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.checkoutSuccess} element={
          <ProtectedRoute>
            <CheckoutSuccess />
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.organization} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <OrganizationHub />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.onboarding.wizard} element={
          <ProtectedRoute>
            <OnboardingWizard />
          </ProtectedRoute>
        } />
        {/* Legacy redirect for /onboarding to dashboard */}
        <Route path="/onboarding" element={<Navigate to={ROUTES.app.dashboard} replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </RealtimeProvider>
  </RestaurantProvider>
);

const LAST_VERSION_KEY = 'alacart-last-app-version';

const App = () => {
  // Clear corrupted cache when version changes + register service worker
  useEffect(() => {
    const handleVersionChange = async () => {
      const lastVersion = localStorage.getItem(LAST_VERSION_KEY);
      
      if (lastVersion !== APP_VERSION) {
        if (import.meta.env.DEV) {
          console.log(`[App] Version changed from ${lastVersion} to ${APP_VERSION}, clearing caches...`);
        }
        
        // Clear IndexedDB React Query cache
        try {
          const deleteRequest = indexedDB.deleteDatabase('alacart-query-cache');
          deleteRequest.onsuccess = () => {
            if (import.meta.env.DEV) {
              console.log('[App] IndexedDB cache cleared successfully');
            }
          };
        } catch (e) {
          if (import.meta.env.DEV) {
            console.warn('[App] Failed to clear IndexedDB:', e);
          }
        }
        
        // Update stored version
        localStorage.setItem(LAST_VERSION_KEY, APP_VERSION);
      }
    };
    
    handleVersionChange();
    registerServiceWorker();
  }, []);

  return (
    <ErrorBoundary>
        <PersistQueryClientProvider 
          client={queryClient} 
          persistOptions={{ 
            persister,
            maxAge: 12 * 60 * 60 * 1000, // 12 hours - aligned with gcTime
            buster: `${APP_VERSION}-v13`, // v13: Master perf optimization - staleTimes, memo, realtime
          }}
        >
        <CMVRecalculationProvider>
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <Suspense fallback={<LoadingScreen />}>
                <Routes>
                  {/* Rotas públicas - SEM RestaurantProvider */}
                  <Route path="/" element={<Landing />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  
                  {/* Integration callback routes */}
                  <Route path={ROUTES.integrations.blingCallback} element={<BlingCallback />} />
                  <Route path={ROUTES.integrations.contaAzulCallback} element={<ContaAzulCallback />} />
                  
                  {/* Approval flow routes */}
                  <Route path="/pending-approval" element={<PendingApproval />} />
                  <Route path="/access-denied" element={<AccessDenied />} />
                  <Route path="/approval-success" element={<ApprovalSuccess />} />
                   <Route path="/permission-denied" element={<PermissionDenied />} />
                   <Route path="/account-suspended" element={<AccountSuspended />} />

                  {/* Rotas de suporte - FORA do RestaurantProvider */}
                  <Route path="/internal-support-alc2024">
                    <Route index element={<SupportLogin />} />
                    <Route path="login" element={<SupportLogin />} />
                    <Route element={<SupportGuard><SupportLayout /></SupportGuard>}>
                      <Route path="dashboard" element={<SupportDashboard />} />
                      <Route path="clients" element={<SupportClients />} />
                      <Route path="clients/:id" element={<ClientDetails />} />
                      <Route path="metrics" element={<SupportMetricsPage />} />
                      <Route path="notifications" element={<SupportNotifications />} />
                      
                      <Route path="notifications-metrics" element={<SupportNotificationsMetrics />} />
                      <Route path="tickets" element={<SupportTickets />} />
                      <Route path="audit-logs" element={<SupportAuditLogs />} />
                      <Route path="approvals" element={<SupportApprovals />} />
                      <Route path="coupons" element={<SupportCoupons />} />
                      <Route path="referrals" element={<SupportReferrals />} />
                      <Route path="retention-offers" element={<SupportRetentionOffers />} />
                    </Route>
                  </Route>

                  {/* Rotas do App principal - DENTRO do RestaurantProvider */}
                  <Route path="/*" element={<MainAppRoutes />} />
                </Routes>
              </Suspense>
            </AuthProvider>
          </BrowserRouter>
        </CMVRecalculationProvider>
      </PersistQueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
